import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from '../../../../common/_custom/Dropdown';
import { DropdownOption } from '../../../../common/_custom/Dropdown/dropdownTypes';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { fetchBankStatementData, downloadBankStatementSummary } from '../../../../store/dashboard/bankStatement/apiHelper';
import styles from './BankDataTable.module.scss';
import EmptyBank from './components/EmptyBank/EmptyBank';
import Header from './components/Header';
import Rows from './components/Rows';
import BankingOverview from './components/BankingOverview/BankingOverview';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
    bankIconBlack,
    leftPurpleArrow,
    downloadIconDataRoomHovered,
    BackButtonLeftFill,
    slantArrowPurple,
} from '../../../../assets/hostedassets';
import arrowRight from '../../../../assets/arrow-right.svg';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { AGGREGATED } from '../../../../utils/constants/commonConstants';
import { isEmpty } from 'lodash';

type BankTableData = {
    isFetched: boolean;
    accounts?: string;
    summaryData?: SingleAccountData[];
    bankingOverview?: BankingOverviewData[];
};

type BankingOverviewData = {
    accountId: string;
    name: string;
    accountNumber: string;
    closingBalance: number;
    closingBalanceDate: string | null;
    odSanctionLimit: number;
    transactionsLTM: number;
    missingDays: number;
};

type SingleAccountData = {
    accountNumber: string;
    bankName: string;
    headers: any[];
    rows: any[];
};

interface BankDataTableProps {
    setDashboardTab?: (tab: { selectedTab: string; tabType: string }) => void;
}

function BankDataTable({ setDashboardTab }: BankDataTableProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tableData, setTableData] = useState<BankTableData>({
        isFetched: false,
    });
    const params = useParams();
    const [selectedAccount, setSelectedAccount] = useState<null | DropdownOption>(null);
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
    const [loadingData, setLoadingData] = useState(false);
    const [downloadingBankData, setDownloadingBankData] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);
    const noBanksConnected = tableData?.isFetched && !tableData?.summaryData?.length;
    const location = useLocation();

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedAccount) {
            appendSeachParams(`${selectedAccount.id}`);
        }
    }, [selectedAccount]);

    useEffect(() => {
        return () => {
            const currentTab = searchParams.get('selectedTab');
            if (currentTab !== 'auditedFinancials' && searchParams.get('financialsType') === 'cashflowFromBanking') {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('financialsType');
                setSearchParams(newSearchParams, { state: location.state });
            }
        };
    }, []);

    const appendSeachParams = (selectedBankId = '', selectedTab = '', financialsType = '') => {
        const paramsToUpdate = {
            ...(selectedBankId && { selectedBankId }),
            ...(selectedTab && { selectedTab }),
            ...(financialsType && { financialsType }),
        };

        updateQueryParams(searchParams, setSearchParams, paramsToUpdate, location);
    };

    const fetchData = () => {
        setLoadingData(true);
        fetchBankStatementData(
            {},
            {
                onSuccess: (data: any = {}) => {
                    setLoadingData(false);

                    // Handle completely empty API response
                    if (!data || isEmpty(data)) {
                        setTableData({
                            isFetched: true,
                            accounts: undefined,
                            summaryData: [],
                            bankingOverview: [],
                        });
                        return;
                    }

                    setTableData({
                        isFetched: true,
                        accounts: data.accounts,
                        summaryData: data.summaryData || [],
                        bankingOverview: data.bankingOverview || [],
                    });

                    const firstAccount = data?.summaryData?.[0];
                    let selectedAccountFromParams = data?.summaryData?.find(
                        (account) => searchParams.get('selectedBankId') === (account?.accountNumber || account?.bankName),
                    );
                    if (selectedAccountFromParams) {
                        setSelectedAccount({
                            id: selectedAccountFromParams?.accountNumber || selectedAccountFromParams?.bankName,
                            label: `${selectedAccountFromParams?.bankName}${
                                selectedAccountFromParams?.accountNumber ? ': ' + selectedAccountFromParams?.accountNumber : ''
                            }`,
                        });
                    } else if (firstAccount) {
                        setSelectedAccount({ id: 'Aggregated', label: 'Aggregated' });
                    }
                },
                onError: () => {
                    setLoadingData(false);
                    setTableData((tableData) => ({
                        ...tableData,
                        isFetched: true,
                    }));
                },
            },
        );
    };

    const dropdownOptions = useMemo(
        () =>
            tableData?.summaryData?.map((accountData) => ({
                id: accountData.accountNumber || accountData.bankName,
                label: `${accountData.bankName}${accountData.accountNumber ? ': ' + accountData.accountNumber : ''}`,
            })),
        [tableData?.summaryData],
    );

    const selectedTableData = useMemo((): any => {
        if (!selectedAccount?.id) return null;

        // For aggregated view
        if (selectedAccount.id.toString().toLowerCase() === AGGREGATED) {
            return tableData?.summaryData?.find((accountData) => accountData.bankName === 'Aggregated');
        }

        // For specific accounts, find directly in summaryData using the dropdown ID
        return tableData?.summaryData?.find(
            (accountData) =>
                (accountData.accountNumber && accountData.accountNumber === selectedAccount.id) ||
                (accountData.bankName && accountData.bankName === selectedAccount.id),
        );
    }, [selectedAccount?.id, tableData?.summaryData, loadingData]);

    const handleAccountSelectFromOverview = (accountId: string) => {
        if (accountId.toString().toLowerCase() === AGGREGATED) {
            setSelectedAccount({ id: 'Aggregated', label: 'Aggregated' });
            setSelectedAccountId('Aggregated');
            return;
        }

        // Get the overview account to find its name
        const bankingOverviewAccount = tableData?.bankingOverview?.find((acc) => acc.accountId === accountId);
        if (!bankingOverviewAccount) return;

        // Find matching account in summaryData using various matching strategies
        const matchingAccount = tableData?.summaryData?.find((accountData) => {
            return bankingOverviewAccount.accountNumber === accountData.accountNumber;
        });

        if (matchingAccount) {
            const selectedOption = {
                id: matchingAccount.accountNumber || matchingAccount.bankName,
                label: `${matchingAccount.bankName}${matchingAccount.accountNumber ? ': ' + matchingAccount.accountNumber : ''}`,
            };
            setSelectedAccount(selectedOption);
            setSelectedAccountId(selectedOption.id);
        }
    };

    const handleAccountSelectFromDropdown = (selectedOption: DropdownOption) => {
        setSelectedAccount(selectedOption);
        setSelectedAccountId(selectedOption.id as string);
    };

    const navigateToCashflowFromBanking = () => {
        if (setDashboardTab) {
            setDashboardTab({ selectedTab: 'auditedFinancials', tabType: 'static' });
            appendSeachParams('', 'auditedFinancials', 'cashflowFromBanking');
        }
    };

    const handleDownloadAllData = () => {
        const orgId = params.id as string;
        if (orgId) {
            setDownloadingBankData(true);
            downloadBankStatementSummary(orgId, 'summary', {
                onSuccess: () => {
                    setDownloadingBankData(false);
                },
                onError: () => {
                    console.error('Failed to download bank statement summary');
                    setDownloadingBankData(false);
                },
            });
        } else {
            console.error('Organization ID not found');
        }
    };

    const noData = useMemo(() => {
        if (tableData.summaryData?.length === 0) return true;
        // Check if headers are empty or missing
        const hasNoHeaders = !selectedTableData?.headers?.length;
        // Check if rows are empty or missing
        const hasNoRows = !selectedTableData?.rows?.length;
        return hasNoHeaders || hasNoRows;
    }, [tableData.summaryData]);

    const getEmptyBankText = () => {
        if (noBanksConnected) return 'No Bank Accounts Connected';
        if (!selectedTableData) return 'Please select the bank account to view';
        if (selectedTableData && (!selectedTableData.headers?.length || !selectedTableData.rows?.length)) {
            return 'Data Not Available';
        }

        return 'Please select the bank account to view';
    };

    const renderContent = () => {
        if (noData) {
            return <EmptyBank text={getEmptyBankText()} />;
        }
        if (noBanksConnected) {
            return <EmptyBank text={getEmptyBankText()} />;
        }

        if (!selectedAccountId) {
            return (
                <BankingOverview
                    data={(tableData.bankingOverview || []) as any}
                    summaryData={(tableData.summaryData || []) as any}
                    onAccountSelect={handleAccountSelectFromOverview}
                />
            );
        }

        return (
            <div className={styles.TableContainer}>
                <table className={styles.BankTable}>
                    <thead className={styles.TableHead}>
                        <Header data={selectedTableData?.headers} />
                    </thead>
                    <tbody className={styles.TableBody}>
                        <Rows rows={selectedTableData?.rows} headers={selectedTableData?.headers} tableRef={tableRef} />
                    </tbody>
                </table>
            </div>
        );
    };

    if (loadingData) {
        return <LoadingSpinner height="60px" style={{ marginTop: '100px' }} color="black" />;
    }

    return (
        <div className={styles.BankTableContainer} ref={tableRef}>
            <div
                style={
                    selectedAccountId
                        ? {
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '1rem',
                              height: '4rem',
                          }
                        : { visibility: 'hidden', marginBottom: '0rem', height: '0rem', display: 'flex', alignItems: 'center' }
                }
            >
                {selectedAccountId && (
                    <>
                        <button onClick={() => setSelectedAccountId(null)} className={styles.BackButton}>
                            <img src={BackButtonLeftFill} alt="Back" style={{ height: '14px', width: '14px' }} />
                            <span
                                style={{
                                    fontFamily: 'Gilroy-Bold,sans-serif',
                                    fontSize: '1rem',
                                }}
                            >
                                Banking Overview
                            </span>
                        </button>
                        <div> {'>'}</div>
                        <div className={styles.AccountName}>{selectedAccount?.label}</div>
                    </>
                )}
            </div>
            <div className={styles.BankWrapper}>
                {!noBanksConnected && (
                    <div className={styles.Top}>
                        <div className={styles.Left}>
                            <h2
                                style={{
                                    fontSize: '1.25rem',
                                    fontFamily: 'Gilroy-Semibold,sans-serif',
                                    margin: '0 0.75rem 0 0',
                                }}
                            >
                                Banking Overview
                            </h2>
                            {selectedAccountId && (
                                <Dropdown
                                    optionsList={dropdownOptions || []}
                                    selectedOption={selectedAccount}
                                    hideClearIcon={true}
                                    itemClickHandler={(selectedOption) => {
                                        setLoadingData(true);
                                        handleAccountSelectFromDropdown(selectedOption);
                                        setTimeout(() => {
                                            setLoadingData(false);
                                        }, 300);
                                    }}
                                    style={{
                                        width: '300px',
                                        fontFamily: 'Gilroy-SemiBold,sans-serif',
                                        borderRadius: '0.5rem',
                                    }}
                                    inputStyle={{ fontFamily: 'Gilroy-SemiBold,sans-serif' }}
                                    placeholder={'Select Account'}
                                    variant="white"
                                    size="tiny"
                                />
                            )}
                        </div>

                        <div className={styles.Right}>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                <div
                                    onClick={handleDownloadAllData}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        cursor: downloadingBankData ? 'default' : 'pointer',
                                        fontFamily: 'Gilroy-SemiBold,sans-serif',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {downloadingBankData ? (
                                        <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                                    ) : (
                                        <img src={downloadIconDataRoomHovered} alt="Download" height={'16px'} />
                                    )}
                                    {downloadingBankData ? 'Downloading...' : 'Download All Data'}
                                </div>
                                <div
                                    onClick={navigateToCashflowFromBanking}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        cursor: 'pointer',
                                        fontFamily: 'Gilroy-SemiBold,sans-serif',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    <img src={slantArrowPurple} alt="" height={'16px'} />
                                    Cashflow From Banking
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.Content}>{renderContent()}</div>
            </div>
        </div>
    );
}

export default BankDataTable;
